import { Button, DialogTrigger, Popover } from '@backstage/ui';
import { RegionChip } from '../RegionChip';
import styles from './HeaderRegions.module.css';

/** Regions shown as chips before the "+N more" chip opens the full list. */
const VISIBLE_REGIONS = 2;

export interface HeaderRegionsProps {
  /** Account display name used as the popover title. */
  accountName: string;
  regions: string[];
}

/**
 * Region chips for the account header subtitle: the first regions inline,
 * a "+N more" chip when the account spans more, and a popover with the
 * full list (an account can span 10+ regions). Region chips show the SVG
 * flag of the region's country, same as the resources table.
 */
export function HeaderRegions({ accountName, regions }: HeaderRegionsProps) {
  if (regions.length === 0) {
    return null;
  }

  const hiddenCount = regions.length - VISIBLE_REGIONS;
  const chips =
    hiddenCount > 0
      ? [...regions.slice(0, VISIBLE_REGIONS), `+${hiddenCount}`]
      : regions;

  const popover = (key: string) => (
    <Popover placement="bottom start" hideArrow>
      <div className={styles.popover}>
        <div
          className={styles.popoverTitle}
        >{`${accountName} — ${regions.length} regions`}</div>
        <div className={styles.popoverList}>
          {regions.map(region => (
            <RegionChip key={`${key}-${region}`} region={region} />
          ))}
        </div>
      </div>
    </Popover>
  );

  return (
    <span className={styles.cell}>
      {chips.map((chip, index) => {
        const isMore = hiddenCount > 0 && index >= VISIBLE_REGIONS;
        return (
          <DialogTrigger key={chip}>
            <Button
              variant="tertiary"
              className={isMore ? `${styles.chip} ${styles.more}` : styles.chipButton}
              aria-label={`Show all ${regions.length} regions of ${accountName}`}
            >
              {isMore ? chip : <RegionChip region={chip} />}
            </Button>
            {popover(chip)}
          </DialogTrigger>
        );
      })}
    </span>
  );
}
